import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { RefreshCw, Trash2, Loader2, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

// Interface para os dados do snapshot recebidos da API
interface SnapshotData {
  id: string;
  magalu_id: string;
  vm_magalu_id: string;
  name: string;
  size: number;
  state?: string;
  status?: string;
  createdAt: string; // Data como string ISO
  updatedAt: string; // Data como string ISO
  vm?: { // Opcional
    nome: string;
    ambiente?: { // Opcional
      nome: string;
    }
  }
}

// Interface para Ambiente (pode ser any se não tivermos um tipo específico)
type Ambiente = any;

// Interface para resposta paginada
interface PaginatedResponse {
  data: SnapshotData[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export function VMBackupList() {
  const [snapshots, setSnapshots] = useState<SnapshotData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [processingDeleteId, setProcessingDeleteId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { addToast } = useToast();

  // Estados para filtros
  const [ambientes, setAmbientes] = useState<Ambiente[]>([]);
  const [selectedAmbiente, setSelectedAmbiente] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  // --- Lógica de Busca de Ambientes e Debounce (similar a VMList) ---
  useEffect(() => {
    const fetchAmbientes = async () => {
      try {
        const response = await api.get<{ data: Ambiente[] }>('/api/ambientes');
        if (response.data && Array.isArray(response.data.data)) {
          setAmbientes(response.data.data);
        } else {
          console.error('API /api/ambientes não retornou a estrutura esperada { data: [...] }:', response.data);
          setAmbientes([]);
        }
      } catch (error) {
        console.error('Erro ao buscar ambientes:', error);
        addToast({ type: 'error', title: 'Erro ao carregar ambientes para filtro' });
        setAmbientes([]);
      }
    };
    fetchAmbientes();
  }, [addToast]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);
  // --- Fim Lógica de Busca de Ambientes e Debounce ---

  // Função para buscar snapshots (modificada)
  const fetchSnapshots = async (page = 1) => { // Resetar para página 1 ao filtrar
    try {
      setLoading(true);
      setError(null);
      const params: any = {
        page,
        pageSize
      };
      // Adicionar filtros aos parâmetros
      if (selectedAmbiente) {
        params.ambienteId = selectedAmbiente;
      }
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm; // Usar termo debounced
      }

      const response = await api.get<PaginatedResponse>('/api/vm-snapshots', { params });
      
      setSnapshots(response.data.data);
      setTotalItems(response.data.total);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.page); // Atualiza página atual
    } catch (err: any) {
      let errorMessage = 'Ocorreu um erro desconhecido';
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      if (errorMessage.includes("is not valid JSON")) {
        errorMessage = "Erro de comunicação com a API. Verifique o proxy do Vite e se o backend está rodando.";
      }
      setError(errorMessage);
      console.error("Erro ao buscar snapshots de VM:", err);
    } finally {
      setLoading(false);
    }
  };

  // Atualizar useEffect para depender dos filtros
  useEffect(() => {
    fetchSnapshots(1); // Busca inicial ou quando filtros mudam
  }, [selectedAmbiente, debouncedSearchTerm]); // Dependências

  // Handler para sincronizar com o Magalu Cloud
  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await api.post('/api/vm-snapshots/sync');
      addToast({
        type: 'success',
        title: 'Sincronização iniciada',
        description: response.data.message || 'Snapshots de VM sincronizados com sucesso'
      });
      // Recarregar a lista após sincronização
      await fetchSnapshots(1); // Voltar para a primeira página após sincronização
    } catch (err: any) {
      console.error("Erro ao sincronizar snapshots de VM:", err);
      let errorMessage = 'Ocorreu um erro ao sincronizar snapshots de VM';
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      addToast({
        type: 'error',
        title: 'Erro na sincronização',
        description: errorMessage
      });
    } finally {
      setSyncing(false);
    }
  };

  // Handler para mudar de página (chama fetchSnapshots com a nova página)
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    fetchSnapshots(page);
  };

  // Handlers para mudança nos filtros
  const handleAmbienteChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAmbiente(event.target.value);
    // A busca será acionada pelo useEffect
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    // O debounce será acionado pelo useEffect
  };

  // Handler para deletar um snapshot
  const handleDelete = async (id: string, name: string) => {
    // Confirmar a exclusão
    if (!window.confirm(`Tem certeza que deseja excluir o snapshot "${name}" do MAGALUCLOUD? Esta ação não pode ser desfeita.`)) {
      return;
    }

    setProcessingDeleteId(id);
    try {
      await api.delete(`/api/vm-snapshots/${id}`);
      addToast({
        type: 'success',
        title: 'Snapshot excluído',
        description: `O snapshot "${name}" foi excluído com sucesso`
      });
      // Atualizar a lista de snapshots
      fetchSnapshots(currentPage);
    } catch (err: any) {
      console.error(`Erro ao excluir snapshot ${id}:`, err);
      let errorMessage = 'Ocorreu um erro ao excluir o snapshot';
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      addToast({
        type: 'error',
        title: 'Erro ao excluir',
        description: errorMessage
      });
    } finally {
      setProcessingDeleteId(null);
    }
  };

  // Função para formatar data
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch (error) {
      console.error("Erro ao formatar data:", error);
      return dateString || 'Data desconhecida';
    }
  };

  // Função para traduzir Estado do Snapshot
  const formatEstado = (estado: string | null | undefined): string => {
    if (!estado) return 'Desconhecido';
    const map: Record<string, string> = {
      available: 'Disponível',
      creating: 'Criando',
      deleting: 'Excluindo',
      error: 'Erro',
      pending: 'Pendente',
      new: 'Novo'
    };
    return map[estado.toLowerCase()] || estado;
  };

  // Função para traduzir Status do Snapshot
  const formatStatus = (status: string | null | undefined): string => {
    if (!status) return 'Desconhecido';
    const map: Record<string, string> = {
      completed: 'Concluído',
      pending: 'Pendente',
      error: 'Erro',
      provisioning: 'Provisionando'
    };
    return map[status.toLowerCase()] || status;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-bold text-gray-800">VMs Backup</h1>
        <button
          onClick={handleSync}
          disabled={syncing}
          className={`flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${syncing ? 'animate-pulse' : ''}`}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Sincronizando...' : 'Sincronizar MGC'}
        </button>
      </div>

      <div className="flex gap-4 mb-6 items-end">
        <div className="w-64">
          <label htmlFor="snapshot-search" className="block text-sm font-medium text-gray-700 mb-1">
            Buscar por nome (snapshot/VM)
          </label>
          <div className="relative">
            <input
              type="text"
              id="snapshot-search"
              placeholder="Digite o nome..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 pr-8"
              disabled={loading || syncing}
            />
            {searchTerm && (
              <button 
                type="button"
                onClick={() => setSearchTerm('')} 
                className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 hover:text-gray-600"
                title="Limpar busca"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="w-64">
          <label htmlFor="snapshot-ambiente-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Ambiente
          </label>
          <div className="relative">
            <select
              id="snapshot-ambiente-filter"
              value={selectedAmbiente}
              onChange={handleAmbienteChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 pr-12 appearance-none"
              disabled={loading || syncing}
            >
              <option value="">Todos Ambientes</option>
              {ambientes.map((ambiente) => (
                <option key={ambiente.id} value={ambiente.id}>
                  {ambiente.nome}
                </option>
              ))}
            </select>
            {selectedAmbiente && (
              <button 
                type="button"
                onClick={() => setSelectedAmbiente('')} 
                className="absolute inset-y-0 right-7 flex items-center pr-2 text-gray-400 hover:text-gray-600"
                title="Limpar seleção"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        {loading && <p className="p-6 text-center text-gray-600">Carregando snapshots...</p>}
        {error && !syncing && <p className="p-6 text-center text-red-600 font-semibold">Erro ao carregar: {error}</p>}
        {!loading && !error && (
          <div className="overflow-x-auto">
            {snapshots.length === 0 ? (
              <p className="p-6 text-center text-gray-600">Nenhum snapshot encontrado.</p>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ambiente</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VM</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tamanho</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Criado em</th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {snapshots.map((snapshot) => {
                      const isDeleting = processingDeleteId === snapshot.id;
                      return (
                        <tr key={snapshot.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{snapshot.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{snapshot.vm?.ambiente?.nome ?? 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{snapshot.vm?.nome ?? 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{`${snapshot.size} GB`}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${snapshot.state === 'available' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {formatEstado(snapshot.state)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${snapshot.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                              {formatStatus(snapshot.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(snapshot.createdAt)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleDelete(snapshot.id, snapshot.name)}
                              disabled={isDeleting}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Excluir Snapshot"
                            >
                              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                
                <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Próximo
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Mostrando <span className="font-medium">{snapshots.length > 0 ? (currentPage - 1) * pageSize + 1 : 0}</span> a <span className="font-medium">{Math.min(currentPage * pageSize, totalItems)}</span> de <span className="font-medium">{totalItems}</span> backups
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Paginação">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage <= 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Anterior</span>
                          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                        </button>
                        
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const pageNum = Math.max(1, Math.min(
                            totalPages - 4, 
                            currentPage - 2
                          )) + i;
                          
                          if (pageNum <= totalPages) {
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`relative inline-flex items-center px-4 py-2 border ${currentPage === pageNum 
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' 
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                } text-sm font-medium`}
                              >
                                {pageNum}
                              </button>
                            );
                          }
                          return null;
                        })}
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage >= totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Próximo</span>
                          <ChevronRight className="h-5 w-5" aria-hidden="true" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
} 